import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  ButtonBase,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import moment from 'moment';

import { TP_COLORS } from '../../../../constants';
import {
  IAdminSortSubTopicVideoLink,
  ISubTopicVideoLink,
  adminSortSubTopicVideoLink,
  getSubTopicState,
} from '../../../../store/SubTopicSlice';
import { getThemeMode } from '../../../../store/ConfigSlice';
import { useAppSelector } from '../../../../hooks';

interface VideoLinkCardProps {
  data: ISubTopicVideoLink;
  onDelete?: () => void;
  onUpdate?: () => void;
  itemIndex: number;
  listLength: number;
}

const buttonStyles: React.CSSProperties = {
  backgroundColor: TP_COLORS.paperGray.light,
  padding: '5px',
  borderRadius: '4px',
  marginRight: '15px',
};

const cardStyles: React.CSSProperties = {
  borderWidth: '2px',
  borderStyle: 'solid',
  borderColor: '#ededed',
  width: 'calc(100% - 49px)',
  borderRadius: '12px',
};

const VideoLinkCard: React.FC<VideoLinkCardProps> = ({
  data,
  itemIndex,
  listLength,
  onDelete = undefined,
  onUpdate = undefined,
}) => {
  const urlParams = useParams();
  const dispatch = useDispatch();

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const themeMode = useAppSelector(getThemeMode);

  // State
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { subTopicId } = urlParams;
  const menuOpen = Boolean(anchorEl);
  const isActionsDisabled = subTopicState.status === 'loading';
  const createdAt = moment(data.createdAt).format('DD MMM YYYY hh:mm A');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSort = (drag: 'up' | 'down') => {
    if (subTopicId) {
      const apiData: IAdminSortSubTopicVideoLink = {
        drag,
        subTopicId,
        videoLinkId: data._id,
      };

      dispatch(adminSortSubTopicVideoLink(apiData));
    }

    handleClose();
  };

  return (
    <>
      <Stack direction="row" alignItems="center">
        <ButtonBase
          id={`li-btn-${itemIndex}`}
          aria-haspopup="true"
          style={buttonStyles}
          onClick={handleClick}
          disabled={isActionsDisabled}
          aria-expanded={menuOpen ? 'true' : undefined}
          aria-controls={menuOpen ? `li-menu-${itemIndex}` : undefined}
        >
          <MenuIcon
            style={{
              color: themeMode === 'dark' ? TP_COLORS.black : undefined,
            }}
          />
        </ButtonBase>
        <Card elevation={0} style={cardStyles}>
          <CardContent style={{ padding: '16px' }}>
            <Stack
              direction="row"
              alignItems="center"
              style={{ width: '100%' }}
            >
              <Stack
                style={{
                  width: 'calc(50% - 10px)',
                  maxWidth: 'calc(50% - 10px)',
                  paddingRight: '10px',
                }}
              >
                <Typography
                  className="ellipsis-text"
                  fontWeight={600}
                  style={{ marginBottom: '10px' }}
                >
                  {data.title}
                </Typography>
                <Typography
                  component="a"
                  href={data.link}
                  target="_blank"
                  className="ellipsis-text"
                  variant="subtitle2"
                  fontWeight={600}
                  color="info.main"
                  style={{ textDecoration: 'underline' }}
                >
                  {data.link}
                </Typography>
              </Stack>
              <div style={{ width: '30%', maxWidth: '30%' }}>
                <Typography
                  color="secondary"
                  title={createdAt}
                  fontWeight={500}
                >
                  {createdAt}
                </Typography>
              </div>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                style={{ width: '20%', maxWidth: '20%' }}
              >
                <IconButton
                  color="secondary"
                  onClick={onUpdate}
                  style={{ marginRight: '10px' }}
                  disabled={isActionsDisabled}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={onDelete}
                  disabled={isActionsDisabled}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      {menuOpen ? (
        <Menu
          id={`li-menu-${itemIndex}`}
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': `li-btn-${itemIndex}`,
          }}
        >
          {itemIndex > 0 ? (
            <MenuItem onClick={() => handleSort('up')}>Drag Top</MenuItem>
          ) : null}
          {listLength - 1 > itemIndex ? (
            <MenuItem onClick={() => handleSort('down')}>Drag Down</MenuItem>
          ) : null}
        </Menu>
      ) : null}
    </>
  );
};

export default VideoLinkCard;
